/**
 * Design tokens for the Guided Course feature (fun.css design system).
 *
 * Consumed via `theme.palette.guidedCourse.*` in styled components so the
 * teammate adding dark mode only needs to update `dark.tsx` — no component
 * files change.
 *
 * Token roles mirror the CSS variables in fun.css:
 *   --gold → gold, --gold-deep → goldDeep, --gold-tint → goldTint, etc.
 */

// ─── Token shape ─────────────────────────────────────────────────────────────

export interface GuidedCourseTokens {
  // Blues (primary action / unlocked / current)
  blue: string;
  blueDeep: string;
  blueTint: string;

  // Greens (completed / correct)
  green: string;
  greenDeep: string;
  greenTint: string;

  // Gold (XP, coins, streak, gold chips)
  gold: string;
  goldDeep: string;
  goldTint: string;

  // Amber (in-progress)
  amber: string;
  amberDeep: string;

  // Purple (AI / special accents)
  purple: string;
  purpleDeep: string;
  purpleTint: string;

  // Red (wrong)
  red: string;
  redDeep: string;
  redTint: string;

  // Lock (locked nodes)
  lock: string;
  lockDeep: string;
  lockTint: string;
  lockTintHover: string;

  // Tint borders — the 2px outline paired with each *Tint fill on soft tiles,
  // chips and callout boxes. A step darker than the tint, a step lighter than
  // the solid, so the shape reads without the weight of a full-strength edge.
  blueTintBorder: string;
  goldTintBorder: string;
  greenTintBorder: string;
  purpleTintBorder: string;

  // Neutral / text / surfaces
  ink: string; // primary text
  ink2: string; // secondary text
  muted: string; // disabled / tertiary
  line: string; // borders / dividers
  bg: string; // page background

  // Gradients
  modeGuidedGrad: string;
  heroGrad: string;

  // Shadows
  shadowSm: string;
  shadow: string;
  shadowLg: string;
  /**
   * Lift under a hovered card. Tighter than `shadow` — a card raises a couple
   * of pixels, not the 10px/30px of a floating panel, and an oversized blur is
   * the first thing to get clipped by a scroll container.
   */
  cardHoverShadow: string;

  // Mode card: guided
  // Map nodes — 3D coin look (6px bottom shadow)
  nodeDoneShadow: string;
  nodeCurrentShadow: string;
  nodeProgressShadow: string;
  nodeLockedShadow: string;

  guidedCardShadow: string;
  guidedCardShadowPressed: string;

  // Mode card: practice
  practiceCardBorder: string;
  practiceCardShadow: string;
  practiceCardShadowPressed: string;

  // "Recommended" badge on the guided card
  recommendedBg: string;
  recommendedColor: string;
  recommendedShadow: string;

  // Fonts (display headings vs body)
  fontDisplay: string;
  fontBody: string;
  fontMono: string;

  // Brand navy (--blue-ink in fun.css)
  blueInk: string;

  // Danger button (exit / quit) accents
  redLight: string; // light pink border/shadow on danger buttons
  redHover: string; // ultra-light pink hover background on danger buttons

  // Misc accents
  blueBorder: string; // medium-light blue for interactive element borders/hover
  goldLight: string; // bright pale gold for star icons and celebration accents

  // Frosted-glass surface (topbar, floating panels)
  surfaceGlass: string;
}

// ─── Light tokens ─────────────────────────────────────────────────────────────

export const guidedCourseLight: GuidedCourseTokens = {
  blue: '#2F74D0',
  blueDeep: '#1E4E8C',
  blueTint: '#E8F0FB',

  green: '#46C93A',
  greenDeep: '#34A52B',
  greenTint: '#E7F8E5',

  gold: '#FFB020',
  goldDeep: '#E08A00',
  goldTint: '#FFF3DC',

  amber: '#FF9F1C',
  amberDeep: '#E0820A',

  purple: '#8B5CF6',
  purpleDeep: '#6D3FE0',
  purpleTint: '#F1ECFE',

  red: '#FF5D5D',
  redDeep: '#D63E3E',
  redTint: '#FFECEC',

  lock: '#C2CBDA',
  lockDeep: '#A4AEC1',
  lockTint: '#EDF0F5',
  lockTintHover: '#E2E6EF',

  blueTintBorder: '#CFE0F6',
  goldTintBorder: '#F6DFB4',
  greenTintBorder: '#C6EDC1',
  purpleTintBorder: '#DCCFFA',

  // Neutral
  ink: '#2A2F45',
  ink2: '#5A627A',
  muted: '#9098AD',
  line: '#E7EBF2',
  bg: '#F3F6FC',

  // Gradients
  modeGuidedGrad: 'radial-gradient(130% 130% at 100% 0%, #3a6fc0, #25507B 70%)',
  heroGrad:
    'radial-gradient(120% 140% at 90% 0%, #3a6fc0 0%, #25507B 55%, #1a3a5c 100%)',

  // Shadows (base colour: rgba(35,53,99, …))
  shadowSm: '0 2px 8px rgba(35,53,99,.07)',
  shadow: '0 10px 30px rgba(35,53,99,.10)',
  shadowLg: '0 24px 60px rgba(20,34,74,.22)',
  cardHoverShadow: '0 6px 16px rgba(35,53,99,.13)',

  // Map nodes
  nodeDoneShadow: '0 6px 0 #34A52B',
  nodeCurrentShadow: '0 6px 0 #1E4E8C',
  nodeProgressShadow: '0 6px 0 #E0820A',
  nodeLockedShadow: '0 6px 0 #A4AEC1',

  // Mode cards
  guidedCardShadow: '0 8px 0 #1E4E8C, 0 10px 30px rgba(35,53,99,.10)',
  guidedCardShadowPressed: '0 2px 0 #1E4E8C, 0 4px 12px rgba(35,53,99,.10)',
  practiceCardBorder: '2px solid #E7EBF2',
  practiceCardShadow: '0 8px 0 #E7EBF2, 0 2px 8px rgba(35,53,99,.07)',
  practiceCardShadowPressed: '0 2px 0 #E7EBF2, 0 1px 4px rgba(35,53,99,.07)',

  // Recommended badge

  recommendedBg: '#FFB020',
  recommendedColor: '#6a4500',
  recommendedShadow: '0 3px 0 #E08A00',

  // Typography
  fontDisplay: '"Baloo 2", "Nunito", system-ui, sans-serif',
  fontBody: '"Nunito", system-ui, sans-serif',
  fontMono: '"JetBrains Mono", ui-monospace, Menlo, Consolas, monospace',

  blueInk: '#25507B',

  redLight: '#F5C0C0',
  redHover: '#FFF5F5',

  blueBorder: '#A8C4F0',
  goldLight: '#FFE27A',

  surfaceGlass: 'rgba(255,255,255,0.92)',
};

// ─── Dark tokens ──────────────────────────────────────────────────────────────
// Teammate: adjust these when dark mode is implemented across the project.
// The guided card gradient and celebration gradients are already dark enough
// and stay the same. Only surfaces, text, borders, and shadows need flipping.

export const guidedCourseDark: GuidedCourseTokens = {
  // Colour accents — slightly lighter so they pop on dark backgrounds
  blue: '#5B9AE8',
  blueDeep: '#2F6AB0',
  blueTint: '#1A2E4A',

  green: '#5DD656',
  greenDeep: '#3EB838',
  greenTint: '#1A3020',

  gold: '#FFBE44',
  goldDeep: '#E09800',
  goldTint: '#2E2210',

  amber: '#FFA83A',
  amberDeep: '#E08A10',

  purple: '#A07CF8',
  purpleDeep: '#7B52E8',
  purpleTint: '#1E1640',

  red: '#FF7A7A',
  redDeep: '#E05050',
  redTint: '#3A1A1A',

  lock: '#4A5268',
  lockDeep: '#363D52',
  lockTint: '#252840',
  lockTintHover: '#2E3154',

  blueTintBorder: '#2C4670',
  goldTintBorder: '#4A3618',
  greenTintBorder: '#274A2C',
  purpleTintBorder: '#332462',

  // Neutral (dark mode surfaces)
  ink: '#E2E6F0',
  ink2: '#A8B0C4',
  // #6B7592 only hit ~3:1 against the card paper (#282d34) — below AA for the
  // 11-12px text it's used on (rating counts, stats, card descriptions).
  muted: '#98A2C4',
  line: '#2E3050',
  bg: '#1B1D2E',

  // Gradients stay the same — the guided card is a dark blue already
  modeGuidedGrad: 'radial-gradient(130% 130% at 100% 0%, #3a6fc0, #25507B 70%)',
  heroGrad:
    'radial-gradient(120% 140% at 90% 0%, #3a6fc0 0%, #25507B 55%, #1a3a5c 100%)',

  // Shadows — blue-tinted, not pure black
  shadowSm: '0 2px 8px rgba(15,17,35,.25)',
  shadow: '0 10px 30px rgba(15,17,35,.35)',
  shadowLg: '0 24px 60px rgba(15,17,35,.55)',
  cardHoverShadow: '0 6px 16px rgba(15,17,35,.45)',

  // Map nodes (dark)
  nodeDoneShadow: '0 6px 0 #34A52B',
  nodeCurrentShadow: '0 6px 0 #1E4E8C',
  nodeProgressShadow: '0 6px 0 #E0820A',
  nodeLockedShadow: '0 6px 0 #4A5268',

  // Mode cards on dark bg
  guidedCardShadow: '0 8px 0 #1E4E8C, 0 10px 30px rgba(15,17,35,.35)',
  guidedCardShadowPressed: '0 2px 0 #1E4E8C, 0 4px 12px rgba(15,17,35,.35)',
  practiceCardBorder: '2px solid #2E3050',
  practiceCardShadow: '0 8px 0 #2E3050, 0 2px 8px rgba(15,17,35,.25)',
  practiceCardShadowPressed: '0 2px 0 #2E3050, 0 1px 4px rgba(15,17,35,.25)',

  // Recommended badge stays vivid
  recommendedBg: '#FFBE44',
  recommendedColor: '#5a3800',
  recommendedShadow: '0 3px 0 #B87000',

  // Typography (unchanged)
  fontDisplay: '"Baloo 2", "Nunito", system-ui, sans-serif',
  fontBody: '"Nunito", system-ui, sans-serif',
  fontMono: '"JetBrains Mono", ui-monospace, Menlo, Consolas, monospace',

  blueInk: '#4A7FB5',

  redLight: '#9B4444',
  redHover: '#3A1A1A',

  blueBorder: '#3A6A9C',
  goldLight: '#FFD95A',

  surfaceGlass: 'rgba(37,40,64,0.94)',
};
